<template>
  <div class="login-modal-inner">

    <!-- Branded Header -->
    <div class="login-brand-header">
      <img src="../../icons/Logo.svg" alt="Renants" class="login-brand-logo" />
      <p class="login-brand-tagline">Your local community marketplace</p>
    </div>

    <!-- Form Body -->
    <div class="login-form-body">

      <!-- BEFORE sending the link -->
      <div v-if="!linkSent">
        <p class="title">Login with Email Link</p>
        <p class="subtitle">We'll send a magic login link straight to your inbox.</p>

        <label>Email</label>
        <input type="email" v-model="email" placeholder="Enter your email" />

        <button class="btn-primary" :disabled="loading" @click="handleMagicLink()">
          <span v-if="loading" class="spinner"></span>
          <span v-else>Send Login Link</span>
        </button>

        <div class="switch-box">
          <span>Already have a password?</span>
          <span class="link" @click="$emit('switchMode', 'login')">Sign In</span>
        </div>
      </div>

      <!-- AFTER sending the link -->
      <div v-else class="success-box">
        <div class="success-icon">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#3467d5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
        </div>
        <p class="title">Check your inbox!</p>
        <p class="subtitle">We sent a login link to <strong>{{ email }}</strong>. Click it to sign in.</p>
        <div class="switch-box">
          <span>Wrong email?</span>
          <span class="link" @click="linkSent = false">Try again</span>
        </div>
        <div class="switch-box" style="margin-top: 6px;">
          <span>Back to</span>
          <span class="link" @click="$emit('switchMode', 'login')">Sign In</span>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { toast } from "vue-sonner";
import axios from 'axios'
import { getRecaptchaToken } from "@/utils/recaptcha";
export default {
  data() {
    return { email: "" , linkSent: false , loading: false};
  },
  methods: {

    isValidEmail(email) {
      return /\S+@\S+\.\S+/.test(email);
    },

    async oldLogin(email) {
      console.log("[oldLogin] email:", email);
      try {
        const recaptchaToken = await getRecaptchaToken('email_link');
        const authRes = await axios.post("/api/authenticateviaemail", { email, recaptchaToken });
        const token = authRes?.data?.token;
        if (!token) {
          console.error("[oldLogin] No token received from /api/authenticateviaemail");
        }

        const userRes = await axios.post("/api/getoneusers", { email });
        const userID = userRes?.data?.[0]?.userID;
        if (!userID) {
          console.error("[oldLogin] No userID found in /api/getoneusers response");
        }

        const sendRecaptchaToken = await getRecaptchaToken('email_link');
        const sendLinkRes = await axios.post("/api/send-emaillink", {
          email,
          token,
          userInfo: userID,
          recaptchaToken: sendRecaptchaToken,
        });
        // Check backend response for email status
        if (sendLinkRes?.data?.failed || sendLinkRes?.data?.skipped) {
          toast.error("Email could not be sent. Please try again later.");
        } else {
          toast.success(`Login link sent to ${email}`);
          this.linkSent = true;
        }
      } catch (err) {
        toast.error("Failed to send login link.");
        
      }
    },


    async verifyLogin(email) {
      const username = email.split("@")[0];

      const recaptchaToken = await getRecaptchaToken('register');
      const registerResponse = await axios.post("/api/register", {
        userName: username,
        email,
        secKey: "TempPass123",
        orgInfo: "Temp",
        recaptchaToken,
      });

      if ([200, 201].includes(registerResponse.status)) {
        console.log("Registration successful");
        await this.oldLogin(email);
      }
    },


    async handleMagicLink() {
      // Guard as well as :disabled — a fast double-click can land two events
      // before Vue has re-rendered the button as disabled.
      if (this.loading) return;
      this.loading = true;

      if (!this.email) {
      toast.error("Please enter an email.");
        this.loading = false;
        return;
      }

      if (!this.isValidEmail(this.email)) {
        toast.error("Invalid email address.");
        this.loading = false;
        return;
      }

      try {
        const email = this.email.trim();

        // add guest
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const guestResponse = await axios.post("/api/addguest", {
          email,
          timezone,
        });
        if (guestResponse.status !== 200) {
          throw new Error("Guest creation failed");
        }

        // check exists
        const checkResponse = await axios.post("/api/getoneusers", { email });

        if (checkResponse.data.length >= 1) {
          await this.oldLogin(email);
        } else {
          await this.verifyLogin(email);
        }
        // Success toast now handled in oldLogin/verifyLogin
      } catch (error) {
        console.error(error);
        toast.error("Failed to send login link.");
      }

      this.loading = false;
    },
  },
};
</script>

<style scoped>
/* ── Brand Header ── */
.login-brand-header {
  background: linear-gradient(135deg, #1d3fa8 0%, #3467d5 60%, #5b87f0 100%);
  border-radius: 14px 14px 0 0;
  padding: 22px 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.login-brand-logo {
  height: 32px;
  filter: brightness(0) invert(1);
}
.login-brand-tagline {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  font-family: var(--font-primary);
  margin: 0;
}

/* ── Form Body ── */
.login-form-body {
  padding: 24px 24px 20px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  line-height: 1.3;
}

.subtitle {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 18px;
  font-family: var(--font-primary);
  line-height: 1.5;
}

label {
  font-size: 13px;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
}

input[type="email"] {
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  margin-bottom: 14px;
  font-size: 14px;
  font-family: var(--font-primary);
  background: #f8faff;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  outline: none;
  color: var(--color-text-primary);
}
input[type="email"]:focus {
  border-color: #3467d5;
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.12);
  background: #fff;
}

/* ── Button ── */
.btn-primary {
  width: 100%;
  padding: 11px 18px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #1d3fa8 0%, #3467d5 60%, #5b87f0 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: var(--font-primary);
  cursor: pointer;
  margin-bottom: 16px;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.btn-primary:hover { opacity: 0.92; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Switch / Footer links ── */
.switch-box {
  display: flex;
  justify-content: center;
  gap: 5px;
  font-size: 13px;
  font-family: var(--font-primary);
  color: var(--color-text-secondary);
  margin-top: 4px;
}

.link {
  color: #3467d5;
  cursor: pointer;
  font-weight: 600;
  font-family: var(--font-primary);
}
.link:hover {
  color: #1d3fa8;
  text-decoration: underline;
}

/* ── Success state ── */
.success-box {
  text-align: center;
  padding: 8px 0 4px;
}
.success-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #eff6ff;
  border: 2px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}
.success-box .title { margin-bottom: 8px; }
.success-box .subtitle { margin-bottom: 20px; }
.success-box strong { color: #1d3fa8; }

/* ── Spinner ── */
.spinner {
  width: 16px;
  height: 16px;
  border: 2.5px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
